import {Dialog, DialogBody, DialogDismiss, DialogFooter} from '@/components/ui/dialog-atoms/DialogAtoms'
import {useTranslation} from '@/translations/i18n'
import {StyledDialogHeader} from './style'
import {Text} from '@/components/ui/text/Text'
import {Button} from '@/components/ui/button/Button'
import {ChooseScaleFormSchema} from '../../formSchemas'
import {Controller, UseFormReturn} from 'react-hook-form'
import dayjs from 'dayjs'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {EvaluationScaleExtended, ScaleType} from '../../types'
import {scaleTypeToLabel} from '../../utils'
import {InputDate} from '@/components/commons/input-date/InputDate'
import {InputText} from '@/components/commons/input-text/InputText'

export const ChooseScaleDialog = ({
    temporaryEvaluations,
    form,
    onSubmit
}: {
    temporaryEvaluations: EvaluationScaleExtended[]
    form: UseFormReturn<ChooseScaleFormSchema>
    onSubmit: () => void
}) => {
    const {t} = useTranslation()
    const handleSubmit = form.handleSubmit(onSubmit)

    const isTotalScoreShown =
        form.watch('type') &&
        form.watch('type') != ScaleType.enum.barthel_index &&
        form.watch('type') != ScaleType.enum.lawton_iadl

    return (
        <Dialog width="600px" overflowY="unset">
            <StyledDialogHeader>
                <Text render="h1" size="textMd" weight="500">
                    {t('projects:x_evaluation_scale', {count: temporaryEvaluations.length + 1})}
                </Text>
            </StyledDialogHeader>
            <DialogBody>
                <Controller
                    control={form.control}
                    name="evaluationDate"
                    render={({field, fieldState}) => (
                        <InputDate
                            label={t('projects:evaluation_date')}
                            placeholder="GG/MM/AAAA"
                            errorMessage={fieldState.error?.message}
                            disableAfterDate={dayjs().toDate()}
                            value={field.value ? dayjs(field.value).toDate() : undefined}
                            onChange={field.onChange}
                        />
                    )}
                />
                <Controller
                    control={form.control}
                    name="type"
                    render={({field, fieldState}) => (
                        <InputSelect
                            placeholder={t('commons:select')}
                            label={t('commons:type')}
                            value={field.value}
                            optionsEntries={ScaleType.options
                                .filter(
                                    scaleType =>
                                        scaleType == form.watch('type') ||
                                        temporaryEvaluations.every(evaluation => evaluation.type != scaleType)
                                )
                                .map(scaleType => [scaleType, t(scaleTypeToLabel[scaleType])])}
                            errorMessage={fieldState.error?.message && t('errors:required')}
                            onChange={value => {
                                form.setValue('totalScore', null)
                                field.onChange(value)
                            }}
                        />
                    )}
                />
                {isTotalScoreShown && (
                    <Controller
                        control={form.control}
                        name="totalScore"
                        render={({field, fieldState}) => (
                            <InputText
                                label={t('commons:total_score')}
                                type="number"
                                value={field.value?.toString()}
                                errorMessage={fieldState.error?.message}
                                onChange={event => {
                                    const value = event.target.value
                                    if (value == '') {
                                        return field.onChange(value)
                                    }
                                    if (isNaN(Number(value))) {
                                        return
                                    }
                                    field.onChange(Number(value))
                                }}
                            />
                        )}
                    />
                )}
            </DialogBody>
            <DialogFooter>
                <DialogDismiss onClick={() => form.reset()} render={<Button variant="secondary" />}>
                    {t('commons:close')}
                </DialogDismiss>
                <Button variant="primaryNeutral" onClick={handleSubmit}>
                    {t('projects:evaluation')}
                </Button>
            </DialogFooter>
        </Dialog>
    )
}
